import { SiteShell, useSite } from "@/components/site/SiteShell";
import LeadForm from "@/pages/LeadForm";
import { Eyebrow } from "@/components/site/Blocks";
import { ArrowLink, Rule, SplitText } from "@/components/site/Motion";
import { siteImg, usePageHead } from "@/lib/siteContent";
import { resolveTiles } from "@/lib/contactTiles";
import { useReveal } from "@/lib/useReveal";
import { SITE_IMG } from "@/lib/siteImages";
import {
  InstagramLogo,
  XLogo,
  YoutubeLogo,
  FacebookLogo,
  GoogleLogo,
} from "@phosphor-icons/react";

const SOCIALS = [
  ["instagram", InstagramLogo],
  ["twitter", XLogo],
  ["youtube", YoutubeLogo],
  ["facebook", FacebookLogo],
  ["google_reviews", GoogleLogo],
];

export default function SiteContact() {
  const site = useSite();
  const shop = site?.shop || {};
  usePageHead({
    title: "Contact & showroom",
    description:
      "Visit the Brioni Granite showroom, call or send a message. Hours, address and directions for our St. Louis stone yard.",
    suffix: shop?.name,
  });
  const contact = site?.config?.contact || {};
  const foot = site?.config?.footer || {};
  const socials = SOCIALS.filter(([key]) => foot[key]);
  const heroImage = siteImg(contact.hero_image, SITE_IMG.kitchenDark);
  const overlay = Math.min(90, Math.max(35, contact.hero_overlay ?? 60));
  useReveal([site]);

  const hours = (contact.hours || []).filter(Boolean);
  const hoursLines = shop.hours_lines?.length
    ? shop.hours_lines
    : hours.length
      ? hours
      : [`Mon–Fri ${shop.hours_start || "08:00"}–${shop.hours_end || "17:00"}`];

  const methods = resolveTiles(contact.tiles, shop, contact, hoursLines);

  return (
    <SiteShell site={site} testId="site-contact" overHero>
      {/* Dark opener */}
      <section className="relative min-h-[60vh] overflow-hidden bg-[color:var(--s-dark)]" data-testid="contact-hero">
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          data-testid="contact-hero-image"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: `rgba(26,26,24,${overlay / 100})` }} />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#1a1a18] to-transparent" />
        <div className="s-container relative flex min-h-[60vh] flex-col justify-end pb-16 pt-36 text-white lg:pb-20">
          <SplitText text="Contact us" className="s-eyebrow s-on-dark" />
          <h1 className="s-h mt-6 max-w-4xl text-4xl sm:text-5xl lg:text-6xl">
            <SplitText text={contact.heading || "Let's price your job"} base={300} />
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-white/75">
            {contact.body ||
              "Send us the basics and the colors you have your eye on — or call the shop and talk to a person. Walk-ins are always welcome."}
          </p>
        </div>
      </section>

      {/* Consultant block — info rows + form */}
      <section className="bg-[color:var(--s-bg)] py-24 sm:py-32">
        <div className="s-container grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <div className="s-reveal">
            <Eyebrow>Contact a consultant</Eyebrow>
            <h2 className="s-hx mt-6 break-words text-4xl sm:text-6xl">Talk to <strong>a person.</strong></h2>
            <Rule className="mt-10" />
            <ul className="mt-2" data-testid="contact-methods">
              {methods.map((m) => {
                const Icon = m.icon;
                const inner = (
                  <>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[color:var(--s-line)] text-[color:var(--s-gold)] transition-colors duration-300 group-hover:border-[color:var(--s-ink)]">
                      <Icon size={18} weight="light" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="s-micro block text-[color:var(--s-mute)]">{m.label}</span>
                      <span className="mt-1 block whitespace-pre-line break-words text-base font-medium leading-snug transition-colors duration-300 group-hover:text-[color:var(--s-gold)]">
                        {m.value}
                      </span>
                    </span>
                  </>
                );
                const cls = "group flex items-start gap-5 border-b border-[color:var(--s-line)] py-6";
                return m.href ? (
                  <li key={m.key}>
                    <a
                      href={m.href}
                      target={m.href.startsWith("http") ? "_blank" : undefined}
                      rel="noreferrer"
                      data-testid={m.testId}
                      className={cls}
                    >
                      {inner}
                    </a>
                  </li>
                ) : (
                  <li key={m.key} data-testid={m.testId} className={cls}>
                    {inner}
                  </li>
                );
              })}
            </ul>

            {socials.length > 0 && (
              <div className="mt-8 flex items-center gap-3">
                {socials.map(([key, Icon]) => (
                  <a
                    key={key}
                    href={foot[key]}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={key}
                    data-testid={`contact-social-${key}`}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--s-line)] text-[color:var(--s-ink)] transition-colors duration-300 hover:bg-[color:var(--s-ink)] hover:text-white"
                  >
                    <Icon size={17} weight="fill" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="s-reveal  bg-[color:var(--s-surface)] p-6 shadow-[0_40px_80px_-60px_rgba(0,0,0,0.35)] sm:p-10" data-delay="1" data-testid="contact-form-card">
            <h3 className="s-hx text-2xl sm:text-3xl">Send information to receive a quote.</h3>
            <div className="lead-embedded mt-6">
              <LeadForm embedded />
            </div>
          </div>
        </div>
      </section>

      {shop.address && (
        <section className="bg-[color:var(--s-surface)] py-24 sm:py-32" data-testid="contact-map">
          <div className="s-container grid gap-14 lg:grid-cols-[1fr_1.3fr] lg:gap-20">
            <div className="s-reveal">
              <Eyebrow>Find the warehouse</Eyebrow>
              <h2 className="s-hx mt-6 break-words text-4xl sm:text-5xl">{shop.address.split("\n")[0]}</h2>
              <ul className="mt-10 border-t border-[color:var(--s-line)]" data-testid="contact-hours">
                {hoursLines.map((l) => (
                  <li key={l} className="flex items-center justify-between border-b border-[color:var(--s-line)] py-4 text-sm">
                    <span className="text-[color:var(--s-mute)]">{l.split(/\s(?=\d)/)[0]}</span>
                    <span className="font-semibold">{l.replace(/^[^\d]*/, "") || "—"}</span>
                  </li>
                ))}
              </ul>
              <ArrowLink
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.address)}`}
                className="mt-10"
                testId="contact-directions"
              >
                Get directions
              </ArrowLink>
            </div>
            <div className="s-reveal s-soft relative aspect-[4/3] bg-[color:var(--s-line)] lg:aspect-auto lg:min-h-[520px]" data-delay="1">
              <iframe
                title="Find our warehouse"
                src={`https://www.google.com/maps?q=${encodeURIComponent(shop.address)}&output=embed`}
                className="absolute inset-0 h-full w-full grayscale-[60%] contrast-[1.05]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>
      )}
    </SiteShell>
  );
}
